import { DownloadOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Divider, Row, Table } from "antd";
import { camelToWords, downloadFileFromUrl, snakeToCamelCase } from "common/utils";
import { DATE_TIME_FORMAT } from "common/constants";
import { useFetchImportHistoryQuery } from "features/dataImport/agencyPortal/dataImportAPI";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import { useFetchUserSummariesQuery } from "features/userManagementTable/userManagementAPI";
import moment from "moment-timezone";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

const StyledHeader = styled.h4`
  margin-top: 0;
  margin-bottom: 36px;
  text-transform: uppercase;
`;

const StyledRow = styled(Row)`
  display: flex;
  width: 100%;
`;

const StyledDivider = styled(Divider)`
  position: absolute;
  top: 28px;
  left: 0;
  background-color: #f9f3f3;
`;

const StyledCard = styled(Card)`
  flex: 1;
  width: 100%;
  .ant-card-body {
    padding: 16px;
    overflow: visible;
  }
  overflow: visible;
  .ant-table-wrapper {
    width: 100%;
  }
  .ant-table {
    width: 100%;
  }
`;

const StyledTable = styled(Table)`
  .ant-table-body {
    overflow-x: auto !important;
  }
`;

const StyledLinkButton = styled(Button)`
  padding: 0;
`;

const StyledErrorFileButton = styled(Button)`
  padding: 0;
  height: auto;
`;

const StyledUploadButton = styled(Button)`
  padding-top: 0px;
  padding-bottom: 10px;
  line-height: 0px;
`;

function CreditorProfileDataImportsTable({ currentCreditor }) {
  const creditorId = currentCreditor?.id;
  const {
    data: {
      [PERMISSIONS.DATA_IMPORT__GET]: isDataImportGetAuthorized,
      [PERMISSIONS.DATA_IMPORT__CREATE]: isDataImportCreateAuthorized,
      [PERMISSIONS.USER__GET]: isUserGetAuthorized,
    },
  } = useAuthorizations();
  const { data: importHistoryData, isLoading } = useFetchImportHistoryQuery(
    { creditorIds: [creditorId] },
    { skip: !creditorId || !isDataImportGetAuthorized },
  );
  const { data: users } = useFetchUserSummariesQuery(
    {},
    { skip: !isUserGetAuthorized || !isDataImportGetAuthorized },
  );
  const userIdToName = useMemo(() => {
    const map = {};
    (users ?? []).forEach((user) => {
      map[user.id] = user.name;
    });
    return map;
  }, [users]);

  const columns = [
    {
      title: "File Name",
      dataIndex: "filename",
      key: "filename",
      render: (filename, record) =>
        record.file ? (
          <StyledLinkButton type="link" onClick={() => downloadFileFromUrl(record.file, filename)}>
            {filename}
          </StyledLinkButton>
        ) : (
          filename
        ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => camelToWords(snakeToCamelCase(type)),
    },
    {
      title: "Upload Time",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => moment(date).format(DATE_TIME_FORMAT),
    },
    {
      title: "Uploaded By",
      dataIndex: "createdById",
      key: "createdById",
      render: (createdById) => userIdToName[createdById] ?? "",
    },
    {
      title: "# of Records Affected",
      dataIndex: "numberOfRowsImported",
      key: "numberOfRowsImported",
      render: (numberOfRowsImported) => numberOfRowsImported ?? "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <>
          <div>{camelToWords(snakeToCamelCase(status))}</div>
          {record.errorFile && (
            <StyledErrorFileButton
              icon={<DownloadOutlined />}
              type="link"
              onClick={() => downloadFileFromUrl(record.errorFile)}
            >
              Download Error File
            </StyledErrorFileButton>
          )}
        </>
      ),
    },
  ];

  if (!isDataImportGetAuthorized) {
    return null;
  }

  return (
    <StyledCard>
      <StyledRow>
        <StyledHeader>Data Imports</StyledHeader>
        {isDataImportCreateAuthorized && (
          <Link to="/data-import" target="_blank" rel="noreferrer">
            <StyledUploadButton type="link" icon={<PlusOutlined />}>
              Upload Data
            </StyledUploadButton>
          </Link>
        )}
        <StyledDivider />
      </StyledRow>
      <StyledTable
        // @ts-ignore
        columns={columns}
        loading={isLoading}
        bordered
        scroll={{ x: true }}
        dataSource={importHistoryData}
        rowKey="id"
        pagination={{ pageSize: 100 }}
      />
    </StyledCard>
  );
}

export default CreditorProfileDataImportsTable;
